/**
 * Browser-side logger. Buffers entries in memory and ships them to the
 * `/api/client-logs` ingest route, which replays them through the server's
 * structured logger (→ stdout + Loki, label app="agent-ui").
 *
 * Delivery is best-effort:
 *  - periodic flush every FLUSH_INTERVAL_MS,
 *  - immediate flush once the buffer reaches MAX_CLIENT_LOG_BATCH,
 *  - a final flush on `pagehide` / tab-hide via `navigator.sendBeacon`, which
 *    survives navigation where a normal `fetch` would be cancelled.
 *
 * SSR-safe: every browser API is touched inside functions guarded on
 * `typeof window`, so importing this module on the server is a no-op.
 */
import {
  CLIENT_LOG_ENDPOINT,
  MAX_CLIENT_LOG_BATCH,
  MAX_CLIENT_LOG_MSG_CHARS,
  type ClientLogEntry,
  type ClientLogLevel,
} from "./client-log-config";

const FLUSH_INTERVAL_MS = 5000;

let buffer: ClientLogEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let installed = false;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function nowIso(): string {
  return new Date().toISOString();
}

function enqueue(level: ClientLogLevel, msg: string, context?: Record<string, unknown>): void {
  if (!isBrowser()) return;
  buffer.push({
    level,
    msg: String(msg).slice(0, MAX_CLIENT_LOG_MSG_CHARS),
    time: nowIso(),
    context: {
      url: window.location?.href,
      ...context,
    },
  });
  if (buffer.length >= MAX_CLIENT_LOG_BATCH) flush();
}

/**
 * Send the buffered batch. With `useBeacon` (page unloading) we prefer
 * `sendBeacon`; otherwise a `keepalive` fetch that can also outlive the page.
 */
function flush(useBeacon = false): void {
  if (!isBrowser() || buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  const body = JSON.stringify({ logs: batch });

  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(CLIENT_LOG_ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch(CLIENT_LOG_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      // Best-effort: drop on network failure rather than retry/grow unbounded.
    });
  } catch {
    // Never let logging throw into application code.
  }
}

/** Public logging surface, mirroring the server logger's `(msg, context)` shape. */
export const clientLogger = {
  debug: (msg: string, context?: Record<string, unknown>) => enqueue("debug", msg, context),
  info: (msg: string, context?: Record<string, unknown>) => enqueue("info", msg, context),
  warn: (msg: string, context?: Record<string, unknown>) => enqueue("warn", msg, context),
  error: (msg: string, context?: Record<string, unknown>) => enqueue("error", msg, context),
  /** Force-send the current buffer (e.g. right after a fatal error). */
  flush: () => flush(false),
};

function serializeError(value: unknown): Record<string, unknown> {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  return { value: typeof value === "string" ? value : JSON.stringify(value) };
}

/** Render a single `console.error(...)` argument to a string for the message. */
function stringifyArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return arg.stack ?? `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

/**
 * Install periodic flushing + global error capture. Idempotent; returns a
 * cleanup function. Call once from a client component mounted in the layout.
 */
export function installClientLogReporter(): () => void {
  if (!isBrowser() || installed) return () => {};
  installed = true;

  flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  flushTimer.unref?.();

  const onError = (event: ErrorEvent) => {
    enqueue("error", event.message || "Uncaught error", {
      kind: "window.onerror",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      ...serializeError(event.error),
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    enqueue("error", "Unhandled promise rejection", {
      kind: "unhandledrejection",
      ...serializeError(event.reason),
    });
  };

  // Forward console.error(...) to Loki while preserving native dev behavior.
  // Our own flush path never calls console.error, so there's no recursion; the
  // `forwarding` guard is belt-and-suspenders against a future caller that does.
  const originalConsoleError = window.console.error.bind(window.console);
  let forwarding = false;
  const patchedConsoleError = (...args: unknown[]) => {
    originalConsoleError(...args);
    if (forwarding) return;
    forwarding = true;
    try {
      const firstError = args.find((a): a is Error => a instanceof Error);
      enqueue("error", args.map(stringifyArg).join(" "), {
        kind: "console.error",
        ...(firstError ? { stack: firstError.stack } : {}),
      });
    } finally {
      forwarding = false;
    }
  };
  window.console.error = patchedConsoleError;

  const onPageHide = () => flush(true);
  const onVisibility = () => {
    if (document.visibilityState === "hidden") flush(true);
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibility);
    // Only restore if nothing else re-patched console.error after us.
    if (window.console.error === patchedConsoleError) {
      window.console.error = originalConsoleError;
    }
    if (flushTimer) clearInterval(flushTimer);
    flushTimer = null;
    installed = false;
    flush(false);
  };
}
